import { useQueryClient } from '@tanstack/react-query';
import { type EventContentObjectDetail, createEventContentObject } from 'models';
import { useMutation } from 'query';
import { getDateType } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { useContentObjectCache } from 'features/feed/queries/cache';
import type { EventContentObjectDataObject } from 'models/service';
import type { DefaultEventData } from '../../components';
import { createProgramEvent } from '../../services';
import { getEventsQueryKey } from '../keys';

const prepareEventsFiles = (eventsFiles: Exclude<DefaultEventData['formValues']['eventsFiles'], undefined>) =>
  eventsFiles.fileList.map((file) => file.response);

export const useCreateProgramEvent = ({ mode }: { mode: 'create' | 'duplicate' }) => {
  const queryClient = useQueryClient();
  const cache = useContentObjectCache();

  return useMutation<
    {
      // TODO need to return DataObject because redux for now
      eventContentDataObject: EventContentObjectDataObject;
      eventContentObject: EventContentObjectDetail;
    },
    { data: DefaultEventData }
  >({
    async mutationFn({ data }) {
      const { eventType, formValues } = data;

      const { data: dataContentObject } = await createProgramEvent({
        /* eslint-disable @typescript-eslint/naming-convention -- BE Api interface */
        community_id: formValues.community?.value,
        cover_photo: formValues.coverPhoto?.[0]?.response,
        duplicate_invitees_from: formValues.duplicateInviteesFromEvent,
        email_invitees:
          mode === 'create'
            ? (formValues.emailInvitees ?? []).map((emailInvitee) => ({
                email: emailInvitee.email,
                event_role: emailInvitee.eventRole,
              }))
            : undefined,
        end_time: getDateType(formValues.endTime),
        event_type: eventType,
        events_files: formValues.eventsFiles ? prepareEventsFiles(formValues.eventsFiles) : undefined,
        featured_in_channels: formValues.featuredInChannels,
        html_content: formValues.htmlContent ?? '',
        invitees:
          mode === 'create'
            ? (formValues.invitees ?? []).map((user) => ({
                event_role: user.eventRole,
                id: user.id,
              }))
            : undefined,
        name: formValues.name,
        program_id: formValues.programId,
        program_topic_id: formValues.programTopicId,
        publish_at: getDateType(formValues.publishAt),
        show_in_channels: formValues.showInChannels,
        start_time: getDateType(formValues.startTime),
        tag_ids: formValues.tags,
        text_content: formValues.textContent ?? '',
        view_permission:
          mode === 'duplicate' && !formValues.duplicateInviteesFromEvent ? 'private' : formValues.viewPermission,
        /* eslint-enable @typescript-eslint/naming-convention -- BE Api interface */
      });

      return {
        eventContentDataObject: dataContentObject,
        eventContentObject: createEventContentObject(dataContentObject),
      };
    },
    async onSuccess(data) {
      cache.create(data.eventContentObject);
      await queryClient.invalidateQueries({ queryKey: getEventsQueryKey({}) });
    },
  });
};
